(()=>{
  const ROOT='[data-dpc-root]';
  const VIDEO='.dpc-card__hover-video';
  const canHover=()=>window.matchMedia('(hover:hover) and (pointer:fine)').matches;
  const perPage=(root)=>{
    if(window.matchMedia('(min-width:62em)').matches){
      const configured=parseInt(root?.dataset?.desktopColumns||'3',10);
      return Number.isFinite(configured)&&configured>0?configured:3;
    }
    return window.matchMedia('(min-width:46em)').matches?2:1;
  };

  function getVideo(card){
    return card?.querySelector(VIDEO)||null;
  }

  function prepareVideo(video){
    if(!video||video.dataset.dpcPrepared==='1')return;

    video.dataset.dpcPrepared='1';
    video.muted=true;
    video.defaultMuted=true;
    video.playsInline=true;
    video.setAttribute('muted','');
    video.setAttribute('playsinline','');

    /*
      Liquid keeps preload="metadata" so videos do not all download at page boot.
      When this section approaches the viewport we upgrade only the relevant videos
      to preload="auto", giving the browser time to buffer before the first hover.
    */
    video.preload='auto';

    try{
      if(video.networkState===HTMLMediaElement.NETWORK_EMPTY||video.readyState<2){
        video.load();
      }
    }catch(_error){}
  }

  function safePlay(card){
    if(!card||!card.classList.contains('dpc-card--has-hover-media'))return;

    const video=getVideo(card);
    if(!video)return;

    card._dpcWantsVideo=true;
    prepareVideo(video);

    const playNow=()=>{
      if(!card._dpcWantsVideo||!card.classList.contains('is-hovered'))return;
      video.muted=true;
      video.playsInline=true;

      const promise=video.play();
      if(promise&&typeof promise.catch==='function'){
        promise.catch(()=>{
          /*
            A first play() can occasionally race the initial Shopify CDN buffer.
            Wait for enough data, then retry once while the pointer is still over
            the card. This removes the old "move out/in to wake the video" behavior.
          */
          if(!card._dpcWantsVideo)return;
          const retry=()=>{
            if(!card._dpcWantsVideo||!card.classList.contains('is-hovered'))return;
            const retryPromise=video.play();
            if(retryPromise&&typeof retryPromise.catch==='function')retryPromise.catch(()=>{});
          };
          video.addEventListener('canplay',retry,{once:true});
        });
      }
    };

    if(video.readyState>=3){
      playNow();
    }else{
      /*
        Call play once immediately as well. Muted inline playback is allowed by
        modern browsers and the promise will simply wait while buffering.
      */
      playNow();

      const resumeWhenReady=()=>{
        if(card._dpcWantsVideo&&card.classList.contains('is-hovered')&&video.paused){
          const p=video.play();
          if(p&&typeof p.catch==='function')p.catch(()=>{});
        }
      };
      video.addEventListener('canplay',resumeWhenReady,{once:true});
    }
  }

  function enterCard(card){
    if(!card||!card.classList.contains('dpc-card--has-hover-media'))return;
    card.classList.add('is-hovered');
    safePlay(card);
  }

  function leaveCard(card){
    if(!card)return;
    card._dpcWantsVideo=false;
    card.classList.remove('is-hovered');

    const video=getVideo(card);
    if(video){
      video.pause();

      /*
        Do NOT reset currentTime to 0 here.
        Keeping the decoded buffer and current position makes the next hover
        resume instantly instead of forcing another seek/decode cycle.
      */
    }
  }

  function pauseAll(root){
    root.querySelectorAll('[data-dpc-card]').forEach(card=>{
      card._dpcWantsVideo=false;
      const video=getVideo(card);
      if(video&&!video.paused)video.pause();
    });
  }

  function warmPage(root,page=root._dpcPage||0){
    if(!canHover())return;

    const cards=[...root.querySelectorAll('[data-dpc-card]')];
    if(!cards.length)return;

    const per=root._dpcPer||perPage(root);
    const start=Math.max(0,page*per);
    const end=Math.min(cards.length,start+per);

    for(let i=start;i<end;i++){
      const video=getVideo(cards[i]);
      if(video)prepareVideo(video);
    }
  }

  function pageCount(root){
    const count=root.querySelectorAll('[data-dpc-slide]').length;
    return Math.max(1,Math.ceil(count/(root._dpcPer||perPage(root))));
  }

  function syncNavigation(root){
    const count=pageCount(root);
    root._dpcPage=Math.max(0,Math.min(root._dpcPage||0,count-1));

    const prev=root.querySelector('[data-dpc-prev]');
    const next=root.querySelector('[data-dpc-next]');
    const navigation=root.querySelector('[data-dpc-navigation]');

    if(navigation){
      navigation.hidden=count<=1;
      navigation.setAttribute('aria-hidden',count<=1?'true':'false');
    }

    if(prev){
      prev.disabled=root._dpcPage===0;
      prev.setAttribute('aria-disabled',prev.disabled?'true':'false');
    }
    if(next){
      next.disabled=root._dpcPage>=count-1;
      next.setAttribute('aria-disabled',next.disabled?'true':'false');
    }

    root.classList.toggle('dpc-is-locked',count<=1);
  }

  function applyPage(root,page,animate=true){
    const track=root.querySelector('[data-dpc-track]');
    const slides=[...root.querySelectorAll('[data-dpc-slide]')];
    if(!track||!slides.length)return;

    root._dpcPer=perPage(root);
    const count=Math.max(1,Math.ceil(slides.length/root._dpcPer));
    root._dpcPage=Math.max(0,Math.min(page,count-1));

    if(count<=1){
      root._dpcPage=0;
    }

    const index=count<=1?0:Math.min(root._dpcPage*root._dpcPer,slides.length-1);
    const target=slides[index];
    const offset=target.offsetLeft;

    if(!animate)track.style.transition='none';
    track.style.transform=`translate3d(${-offset}px,0,0)`;

    if(!animate){
      track.getBoundingClientRect();
      track.style.transition='';
    }

    syncNavigation(root);

    /*
      When users paginate, warm the newly visible page before they hover it.
    */
    warmPage(root,root._dpcPage);
  }

  function bindCardHover(root){
    root.querySelectorAll('[data-dpc-card]').forEach(card=>{
      card.addEventListener('mouseenter',()=>{
        if(canHover())enterCard(card);
      });

      card.addEventListener('mouseleave',()=>{
        if(canHover())leaveCard(card);
      });
    });
  }

  function bindSwipe(root){
    const viewport=root.querySelector('[data-dpc-viewport]');
    if(!viewport)return;

    let startX=0;
    let startY=0;
    let tracking=false;

    viewport.addEventListener('touchstart',event=>{
      const touch=event.touches&&event.touches[0];
      if(!touch)return;
      startX=touch.clientX;
      startY=touch.clientY;
      tracking=true;
    },{passive:true});

    viewport.addEventListener('touchend',event=>{
      if(!tracking)return;
      tracking=false;

      const touch=event.changedTouches&&event.changedTouches[0];
      if(!touch)return;

      const dx=touch.clientX-startX;
      const dy=touch.clientY-startY;

      if(Math.abs(dx)<viewport.clientWidth*.12||Math.abs(dx)<=Math.abs(dy))return;
      applyPage(root,(root._dpcPage||0)+(dx<0?1:-1),true);
    },{passive:true});
  }

  function bindPreloadObserver(root){
    if(!('IntersectionObserver' in window)){
      window.setTimeout(()=>warmPage(root,0),350);
      return;
    }

    /*
      Start buffering shortly BEFORE the section is visible.
      900px gives Shopify's CDN enough time on normal desktop connections while
      avoiding downloading all hover videos immediately at page startup.
    */
    const observer=new IntersectionObserver(entries=>{
      entries.forEach(entry=>{
        if(entry.isIntersecting){
          root._dpcNearViewport=true;
          warmPage(root,root._dpcPage||0);
        }else{
          root._dpcNearViewport=false;
          pauseAll(root);
        }
      });
    },{
      root:null,
      rootMargin:'900px 0px 900px 0px',
      threshold:0
    });

    observer.observe(root);
    root._dpcPreloadObserver=observer;
  }

  function init(root){
    if(!root||root._dpcBound)return;

    root._dpcBound=true;
    root._dpcPer=perPage(root);
    root._dpcPage=0;

    const prev=root.querySelector('[data-dpc-prev]');
    const next=root.querySelector('[data-dpc-next]');

    prev?.addEventListener('click',event=>{
      event.preventDefault();
      if(prev.disabled)return;
      applyPage(root,(root._dpcPage||0)-1,true);
    });

    next?.addEventListener('click',event=>{
      event.preventDefault();
      if(next.disabled)return;
      applyPage(root,(root._dpcPage||0)+1,true);
    });

    bindCardHover(root);
    bindSwipe(root);
    bindPreloadObserver(root);
    applyPage(root,0,false);

    let resizeTimer;
    window.addEventListener('resize',()=>{
      clearTimeout(resizeTimer);
      resizeTimer=setTimeout(()=>{
        const oldPer=root._dpcPer||perPage(root);
        const firstIndex=(root._dpcPage||0)*oldPer;

        root._dpcPer=perPage(root);
        root._dpcPage=Math.floor(firstIndex/root._dpcPer);
        applyPage(root,root._dpcPage,false);
      },120);
    },{passive:true});
  }

  function boot(scope=document){
    const roots=scope.matches?.(ROOT)?[scope]:[...(scope.querySelectorAll?.(ROOT)||[])];
    roots.forEach(init);
  }

  function resumeHoveredCards(){
    if(document.hidden)return;

    document.querySelectorAll(`${ROOT} [data-dpc-card].is-hovered`).forEach(card=>{
      if(canHover())safePlay(card);
    });
  }

  document.readyState==='loading'
    ?document.addEventListener('DOMContentLoaded',()=>boot())
    :boot();

  document.addEventListener('visibilitychange',()=>{
    if(document.hidden){
      document.querySelectorAll(ROOT).forEach(pauseAll);
    }else{
      resumeHoveredCards();
    }
  });

  document.addEventListener('shopify:section:load',event=>boot(event.target));

  document.addEventListener('shopify:section:unload',event=>{
    const root=event.target.matches?.(ROOT)?event.target:event.target.querySelector?.(ROOT);
    root?._dpcPreloadObserver?.disconnect?.();
  });

  document.addEventListener('shopify:block:select',event=>{
    const slide=event.target.closest?.('[data-dpc-slide]');
    if(!slide)return;

    const root=slide.closest(ROOT);
    if(!root)return;

    const slides=[...root.querySelectorAll('[data-dpc-slide]')];
    const index=slides.indexOf(slide);
    if(index<0)return;

    root._dpcPer=perPage(root);
    applyPage(root,Math.floor(index/root._dpcPer),false);
    enterCard(slide.querySelector('[data-dpc-card]'));
  });

  document.addEventListener('shopify:block:deselect',event=>{
    leaveCard(event.target.closest?.('[data-dpc-card]'));
  });
})();
